import React, { useEffect } from 'react';
import { Box, Link, Typography, Paper, useTheme, Chip, Fade } from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import GavelIcon from '@mui/icons-material/Gavel';
import Footer from './Footer';
import AOS from 'aos';
import 'aos/dist/aos.css';

const PrivacyPolicy = () => {
  const theme = useTheme();

  useEffect(() => {
    AOS.init({
      duration: 800,
      once: true,
      easing: 'ease-out-cubic',
      disable: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    });
  }, []);

  const sectionStyle = {
    mb: 4,
    p: 3,
    borderRadius: '16px',
    bgcolor: '#f8fffe',
    border: '1px solid rgba(0, 121, 107, 0.08)',
    transition: 'all 0.3s ease',
    '&:hover': {
      boxShadow: '0 8px 24px -8px rgba(0, 77, 64, 0.1)',
      transform: 'translateY(-2px)',
    },
  };

  const headingStyle = {
    fontWeight: 700,
    mb: 2,
    color: '#00796b',
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    '&::before': {
      content: '""',
      width: 4,
      height: 24,
      borderRadius: 2,
      background: 'linear-gradient(180deg, #00796b 0%, #004d40 100%)',
    },
  };

  return (
    <Box sx={{ bgcolor: theme.palette.background.default, minHeight: '100vh', width: '100%' }}>
      {/* Privacy Policy Section */}
      <Box
        sx={{
          py: { xs: 6, md: 10 },
          px: { xs: 2, md: 4 },
          background: 'linear-gradient(135deg, #e0f7f4 0%, #b2dfdb 50%, #80cbc4 100%)',
          backgroundSize: '400% 400%',
          animation: 'gradient-shift 15s ease infinite',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Animated Background */}
        <Box
          sx={{
            position: 'absolute',
            top: '10%',
            right: '5%',
            width: 200,
            height: 200,
            borderRadius: '50%',
            background: 'rgba(0, 121, 107, 0.1)',
            filter: 'blur(60px)',
            animation: 'float 8s ease-in-out infinite',
          }}
        />

        <Fade in timeout={600}>
          <Paper
            elevation={0}
            sx={{
              maxWidth: 900,
              width: '100%',
              mx: 'auto',
              p: { xs: 4, md: 6 },
              borderRadius: '28px',
              boxShadow: '0 20px 60px -20px rgba(0, 77, 64, 0.2)',
              bgcolor: 'white',
              border: '1px solid rgba(0, 121, 107, 0.1)',
              position: 'relative',
            }}
            data-aos="fade-up"
          >
            {/* Header */}
            <Box sx={{ textAlign: 'center', mb: 5 }}>
              <Chip
                icon={<SecurityIcon sx={{ color: 'white !important' }} />}
                label="Legal Document"
                sx={{
                  background: 'linear-gradient(135deg, #00796b 0%, #004d40 100%)',
                  color: 'white',
                  fontWeight: 600,
                  px: 2,
                  py: 2.5,
                  borderRadius: '50px',
                  mb: 3,
                  boxShadow: '0 4px 20px -4px rgba(0, 121, 107, 0.4)',
                  '& .MuiChip-icon': { color: 'white' },
                }}
              />
              <Typography
                variant="h3"
                sx={{ 
                  fontWeight: 800, 
                  color: '#004d40', 
                  mb: 2,
                  fontSize: { xs: '2rem', md: '2.5rem' },
                }}
              >
                Privacy Policy
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, flexWrap: 'wrap' }}>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                  Effective Date: 31 July 2025
                </Typography>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                  Last Updated: 14 January 2026
                </Typography>
              </Box>
            </Box>

            <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.8, color: theme.palette.text.secondary }}>
              This Privacy Policy ("Policy") is published in accordance with the Information Technology Act, 2000 and the rules made thereunder, including the Information Technology (Reasonable Security Practices and Procedures and Sensitive Personal Data or Information) Rules, 2011. This Policy explains how Bixbite Innovations Private Limited ("Bixbite," "we," "our," or "us") handles information in connection with our website at locopilot.bixbite.ai (the "Website") and LoCoPilot, our free desktop AI code editor (the "Application"). LoCoPilot is privacy-first by design: it is a downloadable application that runs on your own computer, your code and AI conversations stay on your machine, and we do not operate a cloud service that collects, stores, or tracks how you use the Application. We do not require you to create an account, we do not charge for the Application, and we do not run analytics or tracking on it.
            </Typography>

            {[
              {
                title: "1. Definitions",
                content: (
                  <>
                    <Typography variant="body1" sx={{ mb: 1.5, lineHeight: 1.8 }}>
                      <strong>Personal Data:</strong> Any information that identifies or can be used to identify an individual.
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1.5, lineHeight: 1.8 }}>
                      <strong>Website:</strong> Our informational website at locopilot.bixbite.ai (also reachable via platform.bixbite.ai), where you can learn about and download LoCoPilot.
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1.5, lineHeight: 1.8 }}>
                      <strong>Application:</strong> The LoCoPilot desktop code editor that you download and run locally on your own computer.
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1.5, lineHeight: 1.8 }}>
                      <strong>Local Models:</strong> AI models that you download and run entirely on your own machine, with no data leaving your device.
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                      <strong>Cloud Providers:</strong> Third-party AI services (such as Anthropic, OpenAI, or Google) that you may optionally connect to using your own API key. When you use them, your data is sent directly to that provider and is governed by their terms and privacy policies, not by Bixbite.
                    </Typography>
                  </>
                ),
              },
              {
                title: "2. Scope",
                content: (
                  <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                    This Policy applies to:<br />
                    • Visitors to our Website (locopilot.bixbite.ai / platform.bixbite.ai)<br />
                    • People who download and use the LoCoPilot Application<br />
                    • People who contact us by email or through our Website contact form<br /><br />
                    The Application processes your code, prompts, and files locally on your device. We do not receive, collect, or store that content. This Policy does not cover the practices of Cloud Providers you choose to connect using your own API key, or other third-party software (such as Ollama or Hugging Face) you install.
                  </Typography>
                ),
              },
              {
                title: "3. Information We Collect",
                content: (
                  <>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#004d40' }}>A. Information You Provide on the Website</Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 2 }}>
                      • Contact Form / Email: If you contact us, we collect your name, email address, and the contents of your message so we can respond and provide support.
                    </Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#004d40' }}>B. Information Collected by the Application</Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 2 }}>
                      • None. The Application does not require an account, does not include analytics or telemetry, and does not send your code, files, prompts, or usage data to Bixbite. Your work, settings, and project memory are stored only on your own computer.
                    </Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#004d40' }}>C. Information Collected Automatically on the Website</Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                      • Like most websites, our hosting provider may keep standard server logs (such as IP address, browser type, and pages requested) for security and to keep the site running. We do not use tracking or advertising cookies, and we do not run third-party analytics that profile you.
                    </Typography>
                  </>
                ),
              },
              {
                title: "4. How We Use Information",
                content: (
                  <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                    • To respond to your enquiries and provide support<br />
                    • To operate, secure, and maintain the Website<br />
                    • To make the Application available for download<br />
                    • To comply with legal obligations and prevent abuse or security threats<br /><br />
                    We do not sell your information, and we do not use it for advertising or profiling.
                  </Typography>
                ),
              },
              {
                title: "5. The LoCoPilot Application & Your Data",
                content: (
                  <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                    LoCoPilot is designed to run locally. Your code, prompts, AI conversations, settings, and project memory remain on your own machine. We do not collect or store them, we do not train any AI models on them, and there is nothing for you to opt out of because no such data is sent to us. When you use Local Models, the entire AI interaction happens on your device with no internet connection required for the model itself.
                  </Typography>
                ),
              },
              {
                title: "6. Cookies & Tracking",
                content: (
                  <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                    The Website does not use tracking, analytics, or advertising cookies. The Application is desktop software and does not set browser cookies. Any third-party content embedded on our Website (for example, a map or a video player) may set its own cookies under the relevant provider's policy.
                  </Typography>
                ),
              },
              {
                title: "7. Optional Cloud AI Providers",
                content: (
                  <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                    Using a Cloud Provider is entirely optional. If you choose to add your own API key (for example, Anthropic, OpenAI, or Google), the prompts and content you send to that provider go directly from your machine to that provider. Bixbite is not a party to that exchange, does not receive or store that content, and does not see your API keys—they are stored locally in the Application. Your use of any Cloud Provider is governed by that provider's own terms and privacy policy.
                  </Typography>
                ),
              },
              {
                title: "8. How We Share Information",
                content: (
                  <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                    We may share the limited information described above with service providers who help us operate the Website and respond to your messages (such as our hosting and email providers); with legal authorities if required by law, regulation, or court order; and during a business transfer such as a merger or acquisition. We do not sell your data to advertisers or any third party.
                  </Typography>
                ),
              },
              {
                title: "9. Legal Basis for Processing (GDPR)",
                content: (
                  <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                    If you are in the European Economic Area (EEA) or the UK, we process the limited personal data you provide under these lawful bases: Consent (when you choose to contact us), Legitimate interests (responding to your enquiries, keeping the Website secure), and Legal obligations (where the law requires it).
                  </Typography>
                ),
              },
              {
                title: "10. Data Retention",
                content: (
                  <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                    • Contact Messages: Retained only for as long as needed to handle your request and for a reasonable period afterwards, then deleted.<br />
                    • Server Logs: Retained for a short period by our hosting provider for security and operational purposes.<br />
                    • Application Data: Not applicable—your code, settings, and project memory live on your own device, and you control their retention and deletion.
                  </Typography>
                ),
              },
              {
                title: "11. Your Rights",
                content: (
                  <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                    Depending on your jurisdiction (e.g., GDPR, CCPA), you may have the right to access, correct, or delete the personal data we hold about you, to object to or restrict its processing, and to request a copy of it. Because we hold very little personal data—essentially only what you send us when you contact us—these requests are usually simple to fulfil. To exercise these rights, email <Link href="mailto:contact@bixbite.ai" underline="always" sx={{ color: '#00796b', fontWeight: 600 }}>contact@bixbite.ai</Link>.
                  </Typography>
                ),
              },
              {
                title: "12. Security Measures",
                content: (
                  <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                    We serve our Website over HTTPS and take reasonable measures to protect the limited information we hold. Because the Application runs locally, the security of the code and data on your device is also subject to your own computer's security. No method of transmission or storage is 100% secure, and we cannot guarantee absolute security.
                  </Typography>
                ),
              },
              {
                title: "13. Children's Privacy",
                content: (
                  <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                    The Website and Application are not directed to children, and we do not knowingly collect personal data from children. If you believe a child has provided us with personal data, please contact us so we can delete it.
                  </Typography>
                ),
              },
              {
                title: "14. Changes to This Privacy Policy",
                content: (
                  <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                    We may update this policy from time to time. The updated version will be posted on this page with a new Updated Date.
                  </Typography>
                ),
              },
              {
                title: "15. Contact Us",
                content: (
                  <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                    If you have any questions or concerns about this Privacy Policy, contact us at:<br />
                    Email: <Link href="mailto:contact@bixbite.ai" underline="always" sx={{ color: '#00796b', fontWeight: 600 }}>contact@bixbite.ai</Link><br />
                    Website: <Link href="https://locopilot.bixbite.ai/" target="_blank" rel="noopener" underline="always" sx={{ color: '#00796b', fontWeight: 600 }}>https://locopilot.bixbite.ai/</Link>
                  </Typography>
                ),
              },
            ].map((section, index) => (
              <Box key={index} component="section" sx={sectionStyle}>
                <Typography variant="h5" sx={headingStyle}>{section.title}</Typography>
                {section.content}
              </Box>
            ))}
          </Paper>
        </Fade>
      </Box>

      {/* Terms and Conditions Section */}
      <Box
        sx={{
          py: { xs: 6, md: 10 },
          px: { xs: 2, md: 4 },
          background: 'linear-gradient(135deg, #b2dfdb 0%, #80cbc4 50%, #4db6ac 100%)',
          backgroundSize: '400% 400%',
          animation: 'gradient-shift 15s ease infinite',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Animated Background */}
        <Box
          sx={{
            position: 'absolute',
            bottom: '10%',
            left: '5%',
            width: 250,
            height: 250,
            borderRadius: '50%',
            background: 'rgba(0, 77, 64, 0.08)',
            filter: 'blur(80px)',
            animation: 'floatReverse 10s ease-in-out infinite',
          }}
        />

        <Fade in timeout={800}>
          <Paper
            elevation={0}
            sx={{
              maxWidth: 900,
              width: '100%',
              mx: 'auto',
              p: { xs: 4, md: 6 },
              borderRadius: '28px',
              boxShadow: '0 20px 60px -20px rgba(0, 77, 64, 0.2)',
              bgcolor: 'white',
              border: '1px solid rgba(0, 121, 107, 0.1)',
              position: 'relative',
            }}
            data-aos="fade-up"
          >
            {/* Header */}
            <Box sx={{ textAlign: 'center', mb: 5 }}>
              <Chip
                icon={<GavelIcon sx={{ color: 'white !important' }} />}
                label="Legal Document"
                sx={{
                  background: 'linear-gradient(135deg, #004d40 0%, #00251a 100%)',
                  color: 'white',
                  fontWeight: 600,
                  px: 2,
                  py: 2.5,
                  borderRadius: '50px',
                  mb: 3,
                  boxShadow: '0 4px 20px -4px rgba(0, 77, 64, 0.4)',
                  '& .MuiChip-icon': { color: 'white' },
                }}
              />
              <Typography
                variant="h3"
                sx={{ 
                  fontWeight: 800, 
                  color: '#004d40', 
                  mb: 2,
                  fontSize: { xs: '2rem', md: '2.5rem' },
                }}
              >
                Terms and Conditions
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, flexWrap: 'wrap' }}>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                  Effective Date: 31 July 2025
                </Typography>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                  Last Updated: 14 January 2026
                </Typography>
              </Box>
            </Box>

            <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.8, color: theme.palette.text.secondary }}>
              These Terms and Conditions ("Terms") govern your access to and use of our website at locopilot.bixbite.ai (the "Website") and LoCoPilot, the free desktop AI code editor provided by Bixbite Innovations Private Limited ("Bixbite," "we," "our," "us") (the "Application"). LoCoPilot is free software that you download and run on your own computer. By downloading, installing, or using the Application or the Website, you agree to these Terms. If you do not agree, you must not use the Application or the Website.
            </Typography>

            {[
              {
                title: "1. Acceptance of Terms",
                text: "By downloading, installing, or using LoCoPilot or our Website, you acknowledge that you have read, understood, and agree to be bound by these Terms. You also agree that these Terms may be updated or modified from time to time, and your continued use of the Application or Website after such changes constitutes acceptance of the updated Terms.",
              },
              {
                title: "2. The Application",
                text: "LoCoPilot is a free desktop code editor with built-in AI assistance that runs locally on your computer. It lets you run AI models on your own machine, or optionally connect to third-party cloud AI providers using your own API key. The Application is provided free of charge for your use.",
              },
              {
                title: "3. License to Use",
                text: "Subject to these Terms, Bixbite grants you a personal, non-exclusive, non-transferable, revocable license to download and use the Application. The Application is licensed, not sold. Unless a separate open-source or other license is expressly provided with the Application, you may not redistribute, sell, or sublicense it.",
              },
              {
                title: "4. No Account Required",
                text: "You do not need to create an account or register to use the Application. You are responsible for the security of your own computer and for any API keys or credentials you choose to store in the Application for optional cloud providers.",
              },
              {
                title: "5. Free of Charge",
                text: "The Application is free to use. We do not charge subscription fees, credits, or other payments for it, and there is no payment gateway associated with the Application. If you choose to connect a third-party cloud AI provider, that provider may charge you separately under its own terms; those charges are between you and that provider.",
              },
              {
                title: "6. Third-Party Models, Providers & Software",
                text: "The Application can work with local models and with optional third-party cloud providers (such as Anthropic, OpenAI, or Google) and tools (such as Ollama or Hugging Face). When you use a third-party provider with your own API key, your data is sent directly to that provider and your use is governed by that provider's terms and privacy policy. Bixbite does not control, endorse, or assume responsibility for any third-party model, provider, or software, and is not a party to your relationship with them.",
              },
              {
                title: "7. Accuracy of Information & AI Limitations",
                text: "AI models—whether local or cloud—may make mistakes, hallucinate, or provide inaccurate, incomplete, or insecure output, including code. Output should not be relied upon as professional, legal, financial, medical, or any other form of expert advice. You are solely responsible for reviewing, testing, and verifying any output, including code, before relying on or deploying it.",
              },
              {
                title: "8. Data, Privacy & Local Operation",
                text: "LoCoPilot runs locally. Your code, prompts, AI conversations, settings, and project memory stay on your own computer. We do not collect, store, transmit, or track this data, and we do not train any AI models on it. The Application does not include analytics or telemetry. Our Privacy Policy explains how the limited information you provide (for example, when you contact us through the Website) is handled.",
              },
              {
                title: "9. Acceptable Use",
                text: "You agree to use the Application lawfully and responsibly. You agree not to: use it for any illegal or unauthorised purpose; use it to generate or distribute harmful, malicious, or infringing content; violate the terms of any third-party provider you connect; or violate any laws applicable in your jurisdiction. You are solely responsible for how you use the Application and any output you create with it.",
              },
              {
                title: "10. Intellectual Property",
                text: "The Application, the Website, and all related software, text, graphics, logos, and trademarks are owned by Bixbite or its licensors and are protected by intellectual property laws. As between you and Bixbite, you retain ownership of your own code and the content you create using the Application; we claim no ownership over your code or your AI-generated output. Rights in any third-party models or software remain with their respective owners.",
              },
              {
                title: "11. Updates",
                text: "We may release updates, new versions, or changes to the Application from time to time. Some features (such as the model catalog) may fetch information over the internet, but the Application is designed to work offline where possible. We are not obligated to provide updates, support, or continued availability of any particular feature.",
              },
              {
                title: "12. Availability & 'As Is' Basis",
                text: "The Application and Website are provided 'as is' and 'as available', without warranties of any kind, whether express or implied, including merchantability, fitness for a particular purpose, and non-infringement, to the maximum extent permitted by law. We do not warrant that the Application will be uninterrupted, error-free, or compatible with your hardware or third-party software, and we may modify or discontinue the Application or Website at any time.",
              },
              {
                title: "13. Limitation of Liability",
                text: "To the maximum extent permitted by law, Bixbite is not liable for any direct, indirect, incidental, consequential, special, or punitive damages arising from or related to your use of (or inability to use) the Application or Website, including any reliance on AI-generated output or loss of data. Because the Application is provided free of charge, this limitation applies fully to the extent the law allows.",
              },
              {
                title: "14. Indemnity",
                text: "You agree to indemnify and hold harmless Bixbite from any claims, damages, or expenses arising out of your use of the Application, your output, or your violation of these Terms or any applicable law or third-party rights.",
              },
              {
                title: "15. Governing Law",
                text: "These Terms are governed by and construed in accordance with the laws of India. Any disputes will be subject to the exclusive jurisdiction of the courts in Hyderabad, India.",
              },
              {
                title: "16. Force Majeure",
                text: "We are not liable for any delay or failure to perform resulting from causes beyond our reasonable control, including but not limited to natural disasters, internet outages, strikes, government actions, or equipment failures.",
              },
              {
                title: "17. Dispute Resolution",
                text: "In the event of a dispute, both you and Bixbite agree to first attempt resolution through informal discussions. If unresolved, disputes will be settled through arbitration or mediation before resorting to legal action in the courts of Hyderabad, India.",
              },
              {
                title: "18. Contact Us",
                content: (
                  <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                    If you have any questions about these Terms, contact us at:<br />
                    Email: <Link href="mailto:contact@bixbite.ai" underline="always" sx={{ color: '#00796b', fontWeight: 600 }}>contact@bixbite.ai</Link><br />
                    Website: <Link href="https://locopilot.bixbite.ai/" target="_blank" rel="noopener" underline="always" sx={{ color: '#00796b', fontWeight: 600 }}>https://locopilot.bixbite.ai/</Link>
                  </Typography>
                ),
              },
            ].map((section, index) => (
              <Box key={index} component="section" sx={sectionStyle}>
                <Typography variant="h5" sx={headingStyle}>{section.title}</Typography>
                {section.content || (
                  <Typography variant="body1" sx={{ lineHeight: 1.8, color: theme.palette.text.secondary }}>
                    {section.text}
                  </Typography>
                )}
              </Box>
            ))}
          </Paper>
        </Fade>
      </Box>

      <Footer />
    </Box>
  );
};

export default PrivacyPolicy;
